// ============================================================
// FILE: GardenerDashboard.js
// LOCATION: C:\gardenmarket\web\src\pages\gardener\GardenerDashboard.js
// ============================================================
// PURPOSE:
//   Gardener's main job feed. Rebuilt as a master-detail layout
//   (matching My Jobs and My Bookings) — list on left, job
//   detail on right when clicked. The detail pane includes an
//   inline quote form and a link to the buyer's public profile.
// ============================================================

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import client from '../../api/client';
import MasterDetailLayout from '../../components/MasterDetailLayout';
import JobFeedRow from './JobFeedRow';
import JobFeedDetail from './JobFeedDetail';
import CreditBalance from '../../components/CreditBalance';
import { colors, radius, space } from '../../theme/theme';

export default function GardenerDashboard() {
    const { user }  = useAuth();
    const [jobs,          setJobs]          = useState([]);
    const [credit,        setCredit]        = useState(null);
    const [canQuote,      setCanQuote]      = useState(true);
    const [blockedReason, setBlockedReason] = useState('');
    const [loading,       setLoading]       = useState(true);
    const [filter,        setFilter]        = useState('');
    const [selectedJobId, setSelectedJobId] = useState(null);

    const loadJobs = () =>
        client.get('/jobs/matched', { params: { match_status: filter || undefined } })
            .then(r => setJobs(r.data.jobs));

    const loadCredit = async () => {
        try {
            const balRes  = await client.get('/credits/balance');
            setCredit(balRes.data.balance);
            const gateRes = await client.get('/credits/can-quote');
            setCanQuote(gateRes.data.can_quote);
            setBlockedReason(gateRes.data.blocked_reason || '');
        } catch {
            setCanQuote(true);
        }
    };

    useEffect(() => {
        setLoading(true);
        Promise.all([loadJobs(), loadCredit()])
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

    const matchedCount = jobs.filter(j => j.is_matched).length;
    const items = jobs.map(j => ({ ...j, id: j.job_id }));

    const filters = [
        { v:'',         l:'All open jobs' },
        { v:'notified', l:'New matches' },
        { v:'quoted',   l:"I've quoted" },
        { v:'declined', l:'Declined' },
    ];

    const listHeader = (
        <div style={styles.listHeader}>
            <div style={styles.filters}>
                {filters.map(f => (
                    <button key={f.v}
                        style={filter === f.v ? styles.filterOn : styles.filterOff}
                        onClick={() => { setFilter(f.v); setSelectedJobId(null); }}>
                        {f.l}
                    </button>
                ))}
            </div>
            <div style={styles.listCount}>{jobs.length} job{jobs.length !== 1 ? 's' : ''}</div>
        </div>
    );

    if (loading) return <div style={styles.loading}>Loading your job feed...</div>;

    return (
        <div style={styles.page}>

            {/* Credit balance banner — replaces trial banner */}
            {credit && (
                <div style={styles.creditRow}>
                    <CreditBalance compact={true} />
                    <Link to="/gardener/credits" style={styles.topUpLink}>Top up credits →</Link>
                </div>
            )}

            {/* Page header */}
            <div style={styles.header}>
                <div>
                    <h1 style={styles.title}>My Job Feed</h1>
                    <p style={styles.sub}>
                        Hi {user?.first_name} —{' '}
                        {canQuote
                            ? `${matchedCount} matched, ${jobs.length} total open jobs in your area`
                            : 'Top up your credits to see new jobs'}
                    </p>
                </div>
                <div style={{display:'flex', alignItems:'center', gap:'12px'}}>
                    <div style={styles.tierBadge}>
                        {user?.tier_code === 'elite' ? '🏆' : user?.tier_code === 'pro' ? '⭐' : user?.tier_code === 'rising' ? '📈' : '🌱'}
                        {' '}{(user?.tier_code || 'standard').toUpperCase()}
                    </div>
                    <Link to="/gardener/profile" style={styles.btnProfile}>Edit Profile</Link>
                </div>
            </div>

            {/* Stats strip */}
            <div className="gh-grid-4" style={styles.stats}>
                {[
                    { n: user?.total_jobs || 0,   l: 'Jobs completed' },
                    { n: user?.avg_rating ? Number(user.avg_rating).toFixed(1) : '—', l: 'Avg rating' },
                    { n: matchedCount,             l: 'Matched jobs' },
                    { n: jobs.filter(j => j.has_quoted).length, l: 'Quotes sent' },
                ].map((s, i) => (
                    <div key={i} style={styles.stat}>
                        <div style={styles.statNum}>{s.n}</div>
                        <div style={styles.statLabel}>{s.l}</div>
                    </div>
                ))}
            </div>

            {/* ── Credit gate ── */}
            {!canQuote ? (
                <div style={styles.blockedWrap}>
                    <div style={styles.blockedIcon}>🔒</div>
                    <h2 style={styles.blockedTitle}>New jobs are paused</h2>
                    <p style={styles.blockedMsg}>
                        {blockedReason || 'Your credit balance is below -$5. Top up to continue seeing and quoting on new jobs.'}
                    </p>
                    <p style={styles.blockedNote}>
                        Your existing bookings in{' '}
                        <Link to="/gardener/bookings" style={{color:colors.brand, fontWeight:600}}>My Bookings</Link>{' '}
                        are unaffected — you can still manage and complete those jobs.
                    </p>
                    <Link to="/gardener/credits" style={styles.blockedBtn}>Top Up Credits →</Link>
                    <div style={styles.blockedFees}>
                        <p style={{fontSize:'0.78rem', color:colors.textFaint, fontWeight:600, marginBottom:'8px'}}>Platform fee per job won:</p>
                        <div className="gh-grid-4">
                            {[{r:'Up to $25',f:'$1'},{r:'$26–$100',f:'$2'},{r:'$101–$500',f:'$5'},{r:'$501+',f:'$10'}].map(t => (
                                <div key={t.r} style={{textAlign:'center', padding:'8px', backgroundColor:colors.bgPage, borderRadius:'8px'}}>
                                    <span style={{display:'block', fontSize:'0.7rem', color:colors.textFaint, marginBottom:'2px'}}>{t.r}</span>
                                    <span style={{display:'block', fontSize:'1rem', fontWeight:700, color:colors.brand}}>{t.f}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            ) : jobs.length === 0 ? (
                <div style={styles.empty}>
                    <div style={{fontSize:'3rem', marginBottom:'16px'}}>🌱</div>
                    <h3 style={{color:colors.brand, marginBottom:'8px'}}>No open jobs in your area yet</h3>
                    <p style={{color:colors.textMuted, fontSize:'0.9rem', marginBottom:'20px', maxWidth:'400px', textAlign:'center'}}>
                        Make sure your service areas are set up on your profile.
                    </p>
                    <Link to="/gardener/profile" style={styles.btnProfile}>Set Up My Profile</Link>
                </div>
            ) : (
                <MasterDetailLayout
                    items={items}
                    selectedId={selectedJobId}
                    onSelect={setSelectedJobId}
                    listHeader={listHeader}
                    columns={['Date', 'Job', 'Status']}
                    renderRow={(job, isSelected, onClick) => (
                        <JobFeedRow key={job.id} job={job} isSelected={isSelected} onClick={onClick} />
                    )}
                    renderDetail={() => (
                        <JobFeedDetail
                            jobId={selectedJobId}
                            canQuote={canQuote}
                            onQuoted={() => loadJobs()}
                        />
                    )}
                    emptyDetailText="Select a job from the list to view its details and submit a quote."
                />
            )}
        </div>
    );
}

const styles = {
    page:        { maxWidth:'1300px', margin:'0 auto', padding:`${space.xl} 24px` },
    loading:     { textAlign:'center', padding:'60px', color:colors.textMuted },
    creditRow:   { position:'relative', marginBottom:'20px' },
    topUpLink:   { position:'absolute', top:'50%', right:'16px', transform:'translateY(-50%)', color:colors.brand, fontWeight:600, fontSize:'0.85rem', textDecoration:'none' },
    header:      { display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'20px' },
    title:       { margin:'0 0 4px', color:colors.brand, fontSize:'1.8rem' },
    sub:         { margin:0, color:colors.textMuted, fontSize:'0.9rem' },
    tierBadge:   { backgroundColor:'#fff3cd', border:'1px solid #ffc107', padding:'4px 12px', borderRadius:radius.pill, fontSize:'0.85rem', fontWeight:'bold' },
    btnProfile:  { backgroundColor:colors.brand, color:'white', padding:'8px 16px', borderRadius:radius.sm, textDecoration:'none', fontSize:'0.9rem', fontWeight:600 },
    // stats grid columns live in responsive.css (.gh-grid-4) so they
    // can collapse to 2-up on mobile; only non-grid props remain here.
    stats:       { marginBottom:'20px' },
    stat:        { background:'white', borderRadius:radius.md, padding:'16px', textAlign:'center', boxShadow:'0 2px 6px rgba(0,0,0,0.06)' },
    statNum:     { fontSize:'1.8rem', fontWeight:'bold', color:colors.brand },
    statLabel:   { fontSize:'0.8rem', color:colors.textMuted, marginTop:'4px' },
    listHeader:  { padding:'10px 16px 4px' },
    filters:     { display:'flex', gap:'6px', marginBottom:'8px', flexWrap:'wrap' },
    filterOff:   { padding:'5px 12px', borderRadius:radius.pill, border:`1px solid ${colors.border}`, background:'white', cursor:'pointer', fontSize:'0.74rem', color:colors.textMuted },
    filterOn:    { padding:'5px 12px', borderRadius:radius.pill, border:`1px solid ${colors.brand}`, background:colors.brand, color:'white', cursor:'pointer', fontSize:'0.74rem', fontWeight:600 },
    listCount:   { fontSize:'0.72rem', color:colors.textFaint, marginBottom:'4px' },
    empty:        { textAlign:'center', padding:'60px 24px', display:'flex', flexDirection:'column', alignItems:'center' },
    blockedWrap:  { textAlign:'center', padding:'48px 24px', display:'flex', flexDirection:'column', alignItems:'center', backgroundColor:'white', borderRadius:radius.lg, border:`1px solid ${colors.border}`, boxShadow:'0 2px 8px rgba(0,0,0,0.06)' },
    blockedIcon:  { fontSize:'3rem', marginBottom:'16px' },
    blockedTitle: { fontSize:'1.4rem', fontWeight:700, color:'#991B1B', marginBottom:'12px' },
    blockedMsg:   { color:colors.textMuted, fontSize:'0.95rem', maxWidth:'480px', marginBottom:'12px', lineHeight:'1.6' },
    blockedNote:  { color:colors.textFaint, fontSize:'0.85rem', maxWidth:'480px', marginBottom:'24px' },
    blockedBtn:   { backgroundColor:'#991B1B', color:'white', padding:'12px 28px', borderRadius:radius.sm, textDecoration:'none', fontWeight:700, fontSize:'1rem', marginBottom:'32px' },
    blockedFees:  { backgroundColor:colors.bgPage, borderRadius:radius.md, padding:'16px 24px', border:`1px solid ${colors.border}`, width:'100%', maxWidth:'480px' },
};
